/**
 * Bridge between the React auth context and the non-React api client.
 *
 * The api client is a plain module (easier to test, no hook plumbing at every call site),
 * so the current id token and the "session died" callback live here.
 */

/**
 * Invoked on a 401. Resolves `true` when the session was renewed and the store now holds a
 * fresh token worth retrying with, `false` when the session is genuinely gone. The handler
 * clears the token itself in the `false` case.
 */
type UnauthorizedHandler = () => boolean | Promise<boolean>;

let idToken: string | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export const sessionStore = {
  getIdToken(): string | null {
    return idToken;
  },
  setIdToken(token: string | null): void {
    idToken = token;
  },
  setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
    unauthorizedHandler = handler;
  },
  /**
   * Called by the api client on any 401 so the app can try to renew the session before giving
   * up. Returns whether a usable token is now in place. Never rejects: a handler that throws is
   * treated as a failed renewal, because a 401 must not become an unhandled rejection inside a
   * query.
   */
  async notifyUnauthorized(): Promise<boolean> {
    if (!unauthorizedHandler) return false;
    try {
      return (await unauthorizedHandler()) === true;
    } catch {
      return false;
    }
  },
  reset(): void {
    idToken = null;
    unauthorizedHandler = null;
  },
};
